import React, {FC, useState, useEffect, useRef, useLayoutEffect} from "react"
import _ from "lodash"
import { RouteComponentProps, navigate } from "@reach/router"
import Isvg from "react-inlinesvg"
import moment from "moment"

import folderService, {Folder} from "data/folder"
import propertyService, {Property} from "data/property"
import calcService from "data/calc"
import automationService from "data/automation"
import underwritingService from "data/underwriting"
import snapshotService from "data/snapshots"
import socketService, * as eventType from "data/socket"
import { alphanum } from "components/table/lib/sort"
import { humanize, localizeNum } from "data/lib/numbers"
import { local, setlocal, clearlocal } from 'data/lib/local-json'
import { folderChannel } from "data/websocket/sharedChannel"
import { isReportOnly } from "data/lib/report-only"
import {sortFoldersFirst, sortName, sortCode, sortValuation, sortDate, sortCalcStatus} from "components/folder/explorer/sort"

import FolderNav from "components/folder/explorer/folder-nav"
import FolderSearch from "components/folder/explorer/folder-search"
import RowCalcStatus from "components/folder/calc/row-calc-status"
import CalcButton from "components/folder/calc/calc-button"
import NewPropertyModal from "components/folder/property-list/new-property-modal"
import { FolderExplorerContainer, GlobalStyleOverrides } from "components/folder/explorer/folder-explorer-styles"
import { DataTable } from "primereact/datatable"
import { Column } from "primereact/column"
import { ContextMenu } from "primereact/contextmenu"
import ConfirmationModal from "components/modal/confirmation-modal"
import DeleteConfirmation from "components/folder/explorer/delete-confirmation"
import { field as _field } from "components/table/lib/column-field"
import SquareSpinner from 'components/spinner/square-spinner'
import { Home, Folder as FolderIcon } from "images/icons/tools/"
import { CaretRightIcon } from "images/icons/general"
import { CommercialIcon, HospitalityIcon, MixedUseIcon, MFRentIcon } from 'images/icons/sidebar'

export type ContextRow = {
  id: number
  type: "property" | "folder" | null
  code: string
  folderid: number
  originalEvent?: any
  multiselect?: any
}

const FolderExplorer: FC<RouteComponentProps> = (props) => {
  const [folders, setFolders] = useState<object[]>([])
  const [properties, setProperties] = useState<Property[]>([])
  const [multiselect, setMultiselect] = useState<object[]>([])
  const [currentFolder, setCurrentFolder] = useState<number|null>(0)
  const [openFolder, setOpenFolder] = useState<number|null>(null)
  const [breadcrumbs, setBreadcrumbs] = useState<string>("")
  const [searchTerm, setSearchTerm] = useState<string>("")
  const [contextRow, setContextRow] = useState<ContextRow>({id: 0, code: '', type: null, folderid: 0})
  const [tableContextEvent, setTableContextEvent] = useState<any>(null)
  const [detailsLoading, setDetailsLoading] = useState<boolean>(false)
  const [currentOp, setCurrentOp] = useState<ContextRow & {opType: string | null}>({id: 0, code: '', type: null, opType: null, folderid: 0})
  const [deleteConfirmation, setDeleteConfirmation] = useState<any>(null)
  const [addPropModal, setAddPropModal] = useState<boolean>(false)
  const [folderCalcTarget, setFolderCalcTarget] = useState<number|null>(null)
  const [folderCalcPropertySubset, setFolderCalcPropertySubset] = useState<object[]|null>(null)
  const [snapshotRestore, setSnapshotRestore] = useState<{message: string, snapshotId: number, folderId: number, showModal: boolean}>({message: '', snapshotId: 0, folderId: 0, showModal: false})
  const [pasteMessage, setPasteMessage] = useState<string>("")
  const [pasteHeader, setPasteHeader] = useState<string>("")
  const [pasteConfirm, setPasteConfirm] = useState<Function>(()=>{})
  const [showPasteConfirmation, setShowPasteConfirmation] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState<string>("")
  const [showErrorConfirmation, setShowErrorConfirmation] = useState<boolean>(false)
  const [isNewFromTemplate, setIsNewFromTemplate] = useState<boolean>(false)
  const field = _field('explorer')

  const copy = [
    {label: 'Copy', icon: '', _cell: true, command: e => {
      let op = {...contextRow, opType: "copy"}
      if(multiselect?.length) op.multiselect = _.cloneDeep(multiselect)
      setCurrentOp(op)
    }}
  ]

  const cut = [
    {label: 'Cut', icon: '', _cell: true, command: e => {
      let op = {...contextRow, opType: "cut"}
      if(multiselect?.length) op.multiselect = _.cloneDeep(multiselect)
      setCurrentOp(op)
    }}
  ]

  const deleteM = [
    {label: 'Delete', icon: '', _cell: true, command: e => {
      showDeleteConfirmation(contextRow)
    }}
  ]

  const addProp = [
    {label: 'Add Property', icon: '', _cell: true, command: e => {
      const folderPath = folderService.getIdPath(contextRow.id)
      setlocal('folderPath', folderPath)
      selectFolder(contextRow.id)
      setAddPropModal(true)
    }}
  ]

  const paste = [
    {
      label: 'Paste', icon: '', _cell: true, command: e => {
        if(!currentOp.type) return
        if(currentOp.type === "folder") {
          const idpath = folderService.getIdPath(contextRow.folderid)
          if (idpath.includes(currentOp.folderid)){
            setErrorMessage(`Folders cannot be moved or copied into themselves or their subfolders.`)
            setShowErrorConfirmation(true)
            return
          }
        }

        if(currentOp.type === "property" && !currentOp.multiselect) {
          if(currentOp.opType === "copy") {
            propertyService.copyProperty(currentOp.id, contextRow.id, currentOp.code)
          } else {
            propertyService.moveProperty(currentOp.id, contextRow.id)
          }
        } else {
          if(currentOp.type === "folder") {
            setPasteMessage(`Would you like to ${currentOp.opType === 'cut' ? 'move' : currentOp.opType} ${currentOp.type}: ${currentOp.code} into ${contextRow.type}: ${contextRow.code}?  Note this cannot be undone.`)
          } else {
            setPasteMessage(`Would you like to ${currentOp.opType === 'cut' ? 'move' : currentOp.opType} ${currentOp.multiselect?.length} properties into ${contextRow.type}: ${contextRow.code}?  Note this cannot be undone.`)
          }
          setPasteHeader(`${_.capitalize(currentOp.opType)} Confirmation`)
          setPasteConfirm(() => confirmPaste)
          setShowPasteConfirmation(true)
        }
      }
    }
  ]

  const confirmPaste = () => {
    if(currentOp.type === "folder") {
      if(currentOp.opType === "copy") {
        folderService.copyFolder(currentOp.id, contextRow.id)
      } else {
        folderService.moveFolder(currentOp.id, contextRow.id)
      }
    } else {
      if(currentOp.opType === "copy") {
        if(currentOp.multiselect?.length) {
          _.each(currentOp.multiselect, (p) => {
            propertyService.copyProperty(p.propertyid, contextRow.id, p.code)
          })
        } else {
          propertyService.copyProperty(currentOp.id, contextRow.id, currentOp.code)
        }
      } else {
        if(currentOp.multiselect?.length) {
          _.each(currentOp.multiselect, (p) => {
            propertyService.moveProperty(p.propertyid, contextRow.id)
          })
        } else {
          propertyService.moveProperty(currentOp.id, contextRow.id)
        }
      }
    }
    setShowPasteConfirmation(false)
  }

  const rename = [
    {label: 'Rename', icon: '', _cell: true, command: e => {
      folderService.setRenameTarget(contextRow.id)
    }}
  ]

  const newFolder = [
    {label: 'New Folder', icon: '', _cell: true, command: e => {
      folderService.createFolder(contextRow.id)
    }}
  ]

  const calculate = [
    {label: 'Calculate', icon: '', _cell: true, command: e => {
      if(contextRow.type === "folder") {
        selectFolder(contextRow.id, true)
        setFolderCalcTarget(contextRow.id)
      } else {
        if(multiselect?.length) {
          selectFolder(_.get(_.first(multiselect), "folderid"), true)
          setFolderCalcPropertySubset(multiselect)
          setMultiselect([])
        } else {
          selectFolder(contextRow.folderid, true)
          calcService.start("folder", [contextRow.id])
        }
      }
    }}
  ]

  const underwriting = [
    {label: 'Underwriting Workbook', icon: '', _cell: true, command: e => {
      if(multiselect?.length) {
        _.each(multiselect, (p) => underwritingService.quickReport(p.propertyid))
      } else {
        underwritingService.quickReport(contextRow.id)
      }
    }}
  ]

  const snapshot = [
    {label: 'Snapshot', icon: '', _cell: true, command: e => {
      if(contextRow.type === "folder") {
        snapshotService.createSnapshot("folder", null, true, contextRow.id, contextRow.id)
      } else {
        snapshotService.createSnapshot("property", null, true, contextRow.id, contextRow.folderid)
      }
    }}
  ]

  const starMenu = isReportOnly() ? [] : [
    ...addProp,
    ...paste
  ]

  const propertyContextMenu = isReportOnly() ?
    [
      ...calculate,
      ...snapshot,
      ...underwriting,
      ...deleteM
    ]
    :
    [
      ...copy,
      ...cut,
      ...calculate,
      ...snapshot,
      ...underwriting,
      ...deleteM
    ]

  const folderContextMenu = isReportOnly() ?
    [
      ...deleteM,
      ...calculate,
      ...snapshot
    ]
    :
    [
      ...newFolder,
      ...addProp,
      ...rename,
      ...copy,
      ...cut,
      ...paste,
      ...deleteM,
      ...calculate,
      ...snapshot
    ]

  const menuRef = useRef<ContextMenu | null>(null)
  const [menu, setMenu] = useState<[]|object[]>(folderContextMenu)

  useEffect(() => {
    const folderSubscription = folderService.stream().subscribe((data) => {
      if (data.state === "ok"){
        setFolders(data.value)
      }
    })

    const propertySubscription = propertyService.stream().subscribe((data) => {
      if (data.state === "ok"){
        setProperties([...data.value].sort((a, b) => alphanum(_.get(a, "code"), _.get(b, "code"))))
      }
    })

    setDetailsLoading(true)
    // initial load effect
    folderService.fetchAllFolders()
      .then((folderRes) => {
        const folderid = _.last(local('folderPath'))
        if(folderid) {
          setCurrentFolder(folderid)

          propertyService.accumulateByFolderId(folderid, false)
            .then((propertyRes) => {
              setProperties([...(propertyRes as Array<Property>)].sort((a, b) => alphanum(_.get(a, "code"), _.get(b, "code"))))
            })
        }
      })
      .finally(() => {
        setDetailsLoading(false)
      })

    const snapshotEventSubscription = snapshotService.eventStream().subscribe(e => {
      if( e.type === "SNAPSHOT_RESTORE_CONF"){
        setSnapshotRestore({
          message: e.payload.message,
          snapshotId: e.payload.snapshotId,
          folderId: e.payload.folderId,
          showModal: true
        })
      }
      if( e.type === "SNAPSHOT_RESTORE_COMPLETE"){
        closeSnapshotModal()
      }
    })

    const search = new URLSearchParams(window.location.search);
    const snapshotUrl = search.get("snapshot")
    if(snapshotUrl) snapshotService.restoreShared(snapshotUrl)

    return function cleanup() {
      folderSubscription.unsubscribe()
      propertySubscription.unsubscribe()
      snapshotEventSubscription.unsubscribe()
    }
  }, [])

  useEffect(() => {
    //refactor this to be called on login once old client/semaphore is removed
    folderService.createSamples()
  }, [])

  useEffect(() => {
    const socketSubscription = socketService.stream().subscribe(socket => {
      switch(true) {
        case socket.eventType === eventType.PROPERTY_COPY_FINISHED_EVENT:
          if(isNewFromTemplate) {
            const prop = socket.event.property
            selectProperty(prop.propertyid, prop.type)
            setIsNewFromTemplate(false)
          }
          break
        case socket.eventType === eventType.PROPERTY_CREATED_FROM_TEMPLATE:
          setIsNewFromTemplate(true)
          break
      }
    })

    return function cleanup() {
      socketSubscription.unsubscribe()
    }
  }, [isNewFromTemplate])

  useLayoutEffect(() => {
    if(searchTerm) return
    const tooltippedElements = document.querySelectorAll('[data-tooltip]');
    if(!tooltippedElements.length) return

    const handleMouseMove = (event) => {
      const el = event.currentTarget;
      const tooltipText = el.getAttribute('data-tooltip');
      if (!tooltipText) return;

      el.classList.add('tooltip-visible');

      const tooltipRect = {
        width: el.offsetWidth,
        height: el.offsetHeight
      };

      const mouseX = event.clientX;
      const mouseY = event.clientY;

      // Position the tooltip near the mouse cursor
      const tooltipX = mouseX - el.offsetWidth / 2;
      const tooltipY = mouseY - el.offsetHeight - 10;

      el.style.setProperty('--tooltip-x', `${tooltipX}px`);
      el.style.setProperty('--tooltip-y', `${tooltipY}px`);
    };

    const handleMouseLeave = (event) => {
      const el = event.currentTarget;
      el.classList.remove('tooltip-visible');
    };

    tooltippedElements.forEach(el => {
      el.addEventListener('mousemove', handleMouseMove);
      el.addEventListener('mouseleave', handleMouseLeave);
    });

    // Cleanup event listeners on unmount
    return () => {
      tooltippedElements.forEach(el => {
        el.removeEventListener('mousemove', handleMouseMove);
        el.removeEventListener('mouseleave', handleMouseLeave);
      });
    };
  }, [folders, properties, searchTerm]);

  useEffect(() => {
    if(currentFolder) {
      setDetailsLoading(true)
      // folder change effect
      propertyService.accumulateByFolderId(currentFolder, false)
        .then((propertyRes) => {
          setProperties([...(propertyRes as Array<Property>)].sort((a, b) => alphanum(_.get(a, "code"), _.get(b, "code"))))
        })
        .finally(() => {
          setDetailsLoading(false)
        })
    }
  }, [currentFolder])

  useEffect(() => {
    // folder nav context menu setting
    if(contextRow.type === "folder") {
      if(contextRow.id === null) setMenu(newFolder)
      else if(_.startsWith(contextRow.code, "*") && contextRow.code !== "*AssetManagement") setMenu(starMenu)
      else setMenu(folderContextMenu)

      _.invoke(menuRef.current, "show", contextRow.originalEvent)
    }
  }, [contextRow])

  useEffect(() => {
    // details panel context menu setting
    if(tableContextEvent) {
      if(contextRow.type === "property") {
        setMenu(propertyContextMenu)
      } else {
        setMenu(folderContextMenu)
      }
      _.invoke(menuRef.current, "show", tableContextEvent)
      setTableContextEvent(null)
    }
  }, [tableContextEvent])

  function handleSearch(e) {
    setSearchTerm(e.target.value)
  }

  function calcStartedCallback() {
    setFolderCalcTarget(null)
    setFolderCalcPropertySubset(null)
  }

  function showDeleteConfirmation(record: ContextRow) {
    automationService.broadcast("MODAL_OPENING", {})
    const msg = multiselect?.length ?
      'Would you like to delete the following properties?  Note this cannot be undone:' :
      `Deleting ${_.capitalize(record.type)}: ${record.code} cannot be undone. Would you like to snapshot the ${_.capitalize(record.type)} first so you can restore it later?`
    setDeleteConfirmation(DeleteConfirmation({
      show: true,
      message: msg,
      multipleItems: multiselect,
      tertiary: multiselect?.length <= 1,
      tertiaryText: "Create Snapshot",
      tertiaryFunc: () => {
        if(record.type === "folder") {
          snapshotService.createSnapshot("folder", null, true, record.id, record.id)
        } else {
          snapshotService.createSnapshot("property", null, true, record.id, record.folderid)
        }
        if (record.type === "folder") {
          folderService.deleteFolder(record.id)
        } else {
          if(multiselect?.length) {
            _.each(multiselect, (p) => {
              propertyService.deleteProperty(p.propertyid)
            })
          } else {
            propertyService.deleteProperty(record.id)
          }
        }
        setMultiselect([])
        setDeleteConfirmation(null)
      },
      close: () => {
        setDeleteConfirmation(null)
        setTimeout(() => automationService.broadcast("MODAL_CLOSING", {}), 200)
      },
      confirm: () => {
        if (record.type === "folder") {
          folderService.deleteFolder(record.id)
        } else {
          if(multiselect?.length) {
            _.each(multiselect, (p) => {
              propertyService.deleteProperty(p.propertyid)
            })
          } else {
            propertyService.deleteProperty(record.id)
          }
        }
        setMultiselect([])
        setDeleteConfirmation(null)
      }
    }))
  }

  function openAddPropertyModal() {
    automationService.broadcast("NEW_PROPERTY_MODAL_OPENING", {})
    setAddPropModal(true)
  }

  function closeSnapshotModal() {
    setSnapshotRestore({message: '', snapshotId: 0, folderId: 0, showModal: false})
  }

  function selectFolder(fid, calc?: boolean) {
    setCurrentFolder(fid)
    folderService.selectFolder(fid)
    if(!calc) propertyService.clearSelectedProperty()
  }

  function selectProperty(pid, type) {
    propertyService.selectProperty(pid)
    navigate("/n/property/settings")
  }

  function selectDetailItem(e) {
    if(_.get(e, "data.propertyid")) {
      selectFolder(_.get(e, "data.folderid"))
      propertyService.selectProperty(_.get(e, "data.propertyid"))
      if(_.get(e, "data.type") === "Hospitality") {
        navigate("/n/hospitality/rooms")
      } else {
        navigate("/n/rent-roll/home")
      }
    } else {
      selectFolder(_.get(e, "data.folderid"))
      setOpenFolder(_.get(e, "data.folderid"))
      setTimeout(() => setOpenFolder(null), 0)
      propertyService.clearSelectedProperty()
    }
  }

  function selectSearchItem(e) {
    setSearchTerm("")
    const folderid = _.get(e, "data.folderid")
    if(_.get(e, "data.propertyid")) {
      propertyService.accumulateByFolderId(folderid, false)
        .then((propertyRes) => {
          setProperties([...(propertyRes as Array<Property>)].sort((a, b) => alphanum(_.get(a, "code"), _.get(b, "code"))))
          selectFolder(folderid)
          propertyService.selectProperty(_.get(e, "data.propertyid"))
          if(_.get(e, "data.type") === "Hospitality") {
            navigate("/n/hospitality/rooms")
          } else {
            navigate("/n/rent-roll/home")
          }
        })
    } else {
      selectFolder(folderid)
      setOpenFolder(folderid)
      setTimeout(() => setOpenFolder(null), 0)
      propertyService.clearSelectedProperty()
    }
  }

  function buildFolderPath() {
    const idPath = folderService.getIdPath(currentFolder)
    const codePath = folderService.getCodePath(currentFolder)
    const path = _.map(codePath, (c) => {
      const folderId = _.get(_.find(folders, (f) => (f.code === c && idPath.includes(f.folderid))), "folderid")
      return (
        <span key={c} className="path-item">
          <Isvg src={CaretRightIcon} className="caret-icon" />
          <span className="path-text" onClick={() => setCurrentFolder(folderId)}>{c}</span>
        </span>
      )
    })
    return path
  }

  function valuationDisplay(rowData, field, fn) {
    let value = _.get(_.find(rowData.valuationdetail, 'defaultScenario'), field)
    if (_.isNil(value)) return '-'
    else return fn(value)
  }

  function showTableContext(originalEvent){
    originalEvent.persist()
    setTableContextEvent(originalEvent)
  }

  function showFolderContext(originalEvent, fid, code) {
    originalEvent.persist()
    setMultiselect([])
    setContextRow({id: fid, type: "folder", code, originalEvent, folderid: fid})
  }

  function onDrag(e, rowData) {
    e.dataTransfer.setData("rowData", JSON.stringify(rowData))
  }

  function onDrop(e, folderid, foldercode) {
    const data = JSON.parse(e.dataTransfer.getData("rowData"))
    if(multiselect.length <= 1) {
      if(_.isNumber(_.get(data, "propertyid"))) {
        propertyService.moveProperty(_.get(data, "propertyid"), folderid)
      } else {
        const movingFolderId = _.get(data, "folderid")
        const movingFolderCode = _.get(data, "code")
        const idpath = folderService.getIdPath(movingFolderId)
        if (idpath.includes(folderid)){
          setErrorMessage(`Folders cannot be moved or copied into themselves or their subfolders.`)
          setShowErrorConfirmation(true)
          return
        }
        setPasteMessage(`Would you like to move Folder: ${movingFolderCode} into Folder: ${foldercode}?  Note this cannot be undone.`)
        setPasteHeader(`Move Confirmation`)
        setPasteConfirm(() => () => {
          folderService.moveFolder(movingFolderId, folderid)
          setShowPasteConfirmation(false)
        })
        setShowPasteConfirmation(true)
      }
    } else {
      _.each(multiselect, (p) => {
        propertyService.moveProperty(p.propertyid, folderid)
      })
      setMultiselect([])
    }
  }

  const currentChildFolders = _.filter(folders, (f) => f.parentfolder === currentFolder)
  const currentChildProperties = _.filter(properties, (p) => p.folderid === currentFolder)
  let details = sortFoldersFirst({order: 1}, sortCode, [...currentChildFolders, ...currentChildProperties])

  return (
    <FolderExplorerContainer id="folder-explorer-container">
      <GlobalStyleOverrides />
      <CalcButton headless={true} folderId={folderCalcTarget} propertySubset={folderCalcPropertySubset} calcStartedCallback={calcStartedCallback}/>
      <NewPropertyModal show={addPropModal} cb={prop => selectProperty(prop.propertyid, prop.type)} close={() => {setAddPropModal(false); automationService.broadcast("NEW_PROPERTY_MODAL_CLOSING", {})}}/>
      <ContextMenu model={menu} ref={menuRef} appendTo={document.querySelector("body")} onHide={() => {}}/>
      <ConfirmationModal
        show={showErrorConfirmation}
        toggleShow={()=>setShowErrorConfirmation(false)}
        title={'Warning'}
        cancelFunc={()=>setShowErrorConfirmation(false)}
        confirmText={'Dismiss'}
        confirmFunc={()=>setShowErrorConfirmation(false)}
        message={errorMessage}
        confirmOnly={true}
        closeOnly={false}
      />
      <ConfirmationModal
        show={showPasteConfirmation}
        toggleShow={()=>setShowPasteConfirmation(false)}
        title={pasteHeader}
        confirmText={'Paste'}
        confirmFunc={pasteConfirm}
        cancelFunc={()=>setShowPasteConfirmation(false)}
        message={pasteMessage}
        confirmOnly={false}
        closeOnly={false}
      />
      <ConfirmationModal
        show={snapshotRestore.showModal}
        toggleShow={closeSnapshotModal}
        title={'Restore Information'}
        confirmText={'Restore'}
        confirmFunc={()=> {
          snapshotService.restore(snapshotRestore.snapshotId, snapshotRestore.folderId)
          selectFolder(snapshotRestore.folderId)
          closeSnapshotModal()
          setMultiselect([])
        }}
        cancelFunc={closeSnapshotModal}
        message={snapshotRestore.message}
        confirmOnly={false}
        closeOnly={false}
      />
      { deleteConfirmation }
      <div className="search"><input type="search" placeholder="Search" value={searchTerm} onChange={handleSearch}></input></div>
      <div className="breadcrumbs">
        <Isvg src={Home} className="icon" />
        <span className="path-text" onClick={() => setCurrentFolder(null)}>rDCF</span>
        {buildFolderPath()}
      </div>
      <FolderNav
        className="folder-nav"
        selectedFolder={currentFolder}
        openFolder={openFolder}
        onSelectFolder={selectFolder}
        onFocusFolder={(fid) => {
          setCurrentFolder(fid)
          setMultiselect([])
          folderChannel.handleFolderSelection(fid)
        }}
        onContext={showFolderContext}
        onDrop={onDrop} />
      <div className="folder-details">
        { detailsLoading && <SquareSpinner /> }
        { !detailsLoading && !searchTerm &&
          <>
            <div className="details-table">
              <DataTable
                id="folder-explorer-table"
                value={details}
                paginator={false}
                scrollable={true}
                onRowDoubleClick={selectDetailItem}
                selection={multiselect}
                selectionMode="multiple"
                onSelectionChange={(e) => {
                  if(!_.get(e, "originalEvent.altKey")) {
                    const properties = _.filter(e.value, (v) => _.has(v, "propertyid"))
                    setMultiselect(properties)
                  }
                }}
                onRowClick={(e) => {
                  if(_.get(e, "originalEvent.altKey")) {
                    if(_.has(e, "data.propertyid")) {
                      const exists = _.find(multiselect, (m) => m.propertyid === e.data.propertyid)
                      let newMultiselect: any[] = []
                      if(exists) {
                        newMultiselect = _.filter(multiselect, (m) => m.propertyid !== e.data.propertyid)
                      } else {
                        newMultiselect = [..._.cloneDeep(multiselect), _.cloneDeep(e.data)]
                      }
                      setMultiselect(newMultiselect)
                    }
                  }
                }}
                onContextMenu={(e) => menuRef.current ? showTableContext(e.originalEvent) : null}
                contextMenuSelection={contextRow}
                onContextMenuSelectionChange={e => {
                  const newContext = _.cloneDeep(e.value)
                  if(_.isNumber(_.get(newContext, "propertyid"))) {
                    setContextRow({id: newContext.propertyid, type: "property", code: newContext.code, originalEvent: e.originalEvent, folderid: newContext.folderid})
                  } else {
                    showFolderContext(e.originalEvent, newContext.folderid, newContext.code)
                  }
                }}
                stateStorage="custom"
                customSaveState={state => localStorage.setItem("folder-explorer-table-preferences", JSON.stringify({rows: state.rows}))}
                customRestoreState={() => JSON.parse(localStorage.getItem("folder-explorer-table-preferences")!)}>
                <Column header="Calc" style={{width: "50px"}} sortable sortFunction={(e) => sortFoldersFirst(e, sortCalcStatus, details)}
                  {...field("calcdetail.status", (rowData) => _.isNumber(_.get(rowData, "propertyid")) ? <RowCalcStatus propertyId={rowData.propertyid} /> : "")}
                />
                <Column header="" style={{width: "25px"}}
                  body={(rowData, _props) => _.get(rowData, "type") === "Commercial" ? <Isvg src={CommercialIcon} className="icon" /> :
                                             _.get(rowData, "type") === "Multifamily" ? <Isvg src={MFRentIcon} className="icon" /> :
                                             _.get(rowData, "type") === "Hospitality" ? <Isvg src={HospitalityIcon} className="icon" /> :
                                             _.get(rowData, "type") === "Mixed-Use" ? <Isvg src={MixedUseIcon} className="icon" /> :
                                             _.isNumber(_.get(rowData, "folderid")) ? <Isvg src={FolderIcon} className="icon" /> : null }
                />
                <Column style={{width: "240px", overflow: "hidden", textOverflow: "ellipsis"}}
                  sortable
                  header="Code"
                  sortFunction={(e) => sortFoldersFirst(e, sortCode, details)}
                  {...field('code', (rowData, props) => <span data-tooltip={rowData.code}>{rowData.code}</span>, undefined, undefined, onDrag)}
                />
                <Column style={{minWidth: "200px", overflow: "hidden", textOverflow: "ellipsis"}}
                  sortable
                  header="Name"
                  sortFunction={(e) => sortFoldersFirst(e, sortName, details)}
                  {...field('name', (rowData, props) => <span data-tooltip={rowData.name}>{rowData.name}</span>, undefined, undefined, onDrag)}
                />
                <Column
                  sortable
                  header="Value"
                  sortFunction={(e) => sortFoldersFirst(e, sortValuation, details)}
                  {...field("lastCalcValue", (rowData) => valuationDisplay(rowData, "lastCalcValue", value => humanize(value, 2, true)))}
                />
                <Column
                  sortable
                  header="Per Area"
                  sortFunction={(e) => sortFoldersFirst(e, sortValuation, details)}
                  {...field("lastCalcValuePerArea", (rowData) => valuationDisplay(rowData, "lastCalcValuePerArea", value => localizeNum(value, 2, true)))}
                />
                {/* <Column
                  sortable
                  header="City"
                  {...field('city')}
                />
                <Column style={{width: "70px"}}
                  sortable
                  header="State"
                  {...field('state')}
                />
                <Column style={{width: "70px"}}
                  sortable
                  header="Zip"
                  {...field('zip')}
                /> */}
                <Column style={{width: "150px"}}
                  sortable
                  header="Last Updated"
                  sortFunction={(e) => sortFoldersFirst(e, sortDate, details)}
                  {...field("activitydetail.lastupdated", (rowData) => _.get(rowData, "activitydetail.lastupdated") ? moment(_.get(rowData, "activitydetail.lastupdated")).format("MM/DD/YYYY hh:mm A") : "-")}
                />
                {/* <Column
                  sortable
                  header="Last Updated By"
                  {...field('activitydetail.user')}
                /> */}
              </DataTable>
            </div>
            <span className="total-items">{details.length && multiselect?.length > 1 ? `${details.length} Items Total - ${multiselect?.length} Selected` : `${details.length} Items Total`}</span>
          </>
        }
        {!detailsLoading && searchTerm && <FolderSearch searchTerm={searchTerm} dblClick={selectSearchItem}/>}
      </div>
    </FolderExplorerContainer>
  )
}

export default FolderExplorer
